import axios from 'axios';
import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RegimeBadge } from "@/components/RegimeBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
// 📏 [v13.63 Phase 5 — 2026-02-28] Visibilité des colonnes km du rapport
import { usePermissions } from "@/lib/PermissionsContext";
import { Clock, Calendar, Search, Download, BarChart2, User, ChevronDown, ChevronRight, Bell, RefreshCw, CheckCheck, X, Trash2, Scissors, SquarePen, Check, Plus, Eye, MessageSquare, ClipboardList } from "lucide-react";
import { BtSearchCombobox } from "../../components/BtSearchCombobox";
import { HistoriqueCommentairesDialog } from "@/components/pointage/HistoriqueCommentairesDialog";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PunchDetailsDrawer } from "@/components/PunchDetailsDrawer";
import { EmployeeSelector } from "@/components/EmployeeSelector";
import api from "@/lib/api";
import { estProprietaire, aLeRole } from '@/lib/roles'; // 👥 E3b — rôles multiples
import { formatLocalTime } from '@/utils/formatLocalTime'; // 🆕 [P1 ext]

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// 🆕 Calcule le dimanche de Pâques pour une année donnée (algorithme de Gauss/Meeus).
function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const L = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * L) / 451);
  const month = Math.floor((h + L - 7 * m + 114) / 31);
  const day = ((h + L - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

// 🆕 Renvoie true si la plage [dDebut, dFin] inclut la fenêtre Pâques [Vendredi saint - 3j, Lundi Pâques + 3j]
// pour l'une des années couvertes par la période.
function rangeOverlapsPaques(dDebut, dFin) {
  if (!dDebut || !dFin) return false;
  const start = new Date(dDebut);
  const end = new Date(dFin);
  if (isNaN(start) || isNaN(end)) return false;
  for (let y = start.getFullYear(); y <= end.getFullYear(); y++) {
    const easter = easterSunday(y);
    const from = new Date(easter); from.setDate(from.getDate() - 5); // Vendredi saint -3j
    const to = new Date(easter); to.setDate(to.getDate() + 4);       // Lundi +3j
    if (end >= from && start <= to) return true;
  }
  return false;
}

function fmtH(h) {
  return (h || 0).toFixed(2).replace('.', ',') + 'h';
}

function fmtTime(dt) {
  if (!dt) return '—';
  return formatLocalTime(dt, { timeOnly: true });
}

// Ligne employé avec historique des punchs approuvés dépliable
function EmployeRow({ emp, allPunches, showKm = false, km = null }) {
  const [expanded, setExpanded] = useState(false);
  const empPunches = allPunches.filter(p => p.employee_id === emp.employee_id || p.employee_nom === emp.employee_nom);
  return (
    <>
      <tr className="border-b hover:bg-slate-50">
        <td className="p-2">
          <div className="flex items-center gap-2">
            <EmployeeAvatar photoUrl={emp.employee_photo_url} prenom={emp.employee_nom?.split(' ')[0] || ''} nom={emp.employee_nom?.split(' ').slice(1).join(' ') || ''} size="xs" />
            <span className="font-medium">{emp.employee_nom}</span>
          </div>
        </td>
        <td className="p-2 text-right text-slate-600">{emp.nb_punches}</td>
        <td className="p-2 text-right font-bold text-green-700">{fmtH(emp.heures_nettes)}</td>
        {/* 🚚 [Phase v13.52] Colonne temps de déplacement (agrégat employé) */}
        <td className="p-2 text-right text-sky-700 font-semibold" data-testid={`emp-temps-deplacement-${emp.employee_id}`}>
          {(emp.temps_deplacement_minutes || 0) > 0
            ? `${emp.temps_deplacement_minutes} min`
            : <span className="text-slate-300">—</span>}
        </td>
        <td className="p-2 text-right font-bold text-emerald-700">
          {fmtH(emp.heures_remunerees_totales ?? emp.heures_nettes)}
        </td>
        {/* 📏 [v13.63 Phase 5] Colonnes kilométrage (conditionnelles) */}
        {showKm && (
          <>
            <td className="p-2 text-right text-teal-700 font-semibold" data-testid={`emp-km-payables-${emp.employee_id}`}>
              {km && km.total_payable_km > 0
                ? `${km.total_payable_km.toFixed(2)} km`
                : <span className="text-slate-300">—</span>}
            </td>
            <td className="p-2 text-right text-teal-700 font-bold" data-testid={`emp-km-montant-${emp.employee_id}`}>
              {km && km.total_montant_dollars > 0
                ? `${km.total_montant_dollars.toFixed(2)} $`
                : <span className="text-slate-300">—</span>}
            </td>
          </>
        )}
        <td className="p-2 text-center">
          {empPunches.length > 0 && (
            <button onClick={() => setExpanded(v => !v)} className="text-slate-500 hover:text-slate-700 flex items-center gap-0.5 mx-auto text-xs">
              {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              {expanded ? 'Masquer' : 'Voir'}
            </button>
          )}
        </td>
      </tr>
      {expanded && empPunches.map((p) => (
        <tr key={p.id || p.punch_id || `${p.employee_id}-${p.punch_in_time}`} className="bg-slate-50 border-b text-xs">
          <td className="pl-8 py-1.5 text-gray-600">
            {formatLocalTime(p.punch_in_time, { dateOnly: true, weekday: 'short', format: 'short', withoutYear: true })}
          </td>
          <td className="py-1.5 text-right text-gray-500 pr-2">
            {fmtTime(p.punch_in_time)} → {fmtTime(p.punch_out_time)}
          </td>
          <td className="py-1.5 text-right font-semibold text-green-700 pr-3">{fmtH(p.heures_nettes)}</td>
          {/* 🚚 [Phase v13.52] Déplacement par punch (readonly ici, édition dans ValidationHeures) */}
          <td className="py-1.5 text-right text-sky-700 font-medium pr-3">
            {(p.temps_deplacement_minutes || 0) > 0
              ? `${p.temps_deplacement_minutes} min`
              : <span className="text-slate-300">—</span>}
          </td>
          <td className="py-1.5 text-right font-semibold text-emerald-700 pr-3">
            {fmtH((p.heures_nettes || 0) + (p.temps_deplacement_heures || 0))}
          </td>
          {showKm && <td colSpan={2} className="py-1.5 text-right text-slate-300 pr-3">—</td>}
          <td className="py-1.5 text-center">
            <Badge className="text-[9px] px-1 py-0 bg-green-100 text-green-700">✓</Badge>
          </td>
        </tr>
      ))}
    </>
  );
}



export function RapportPointage() {
  const { user } = useAuth();
  const isOwner = estProprietaire(user); // Cat H — identité Propriétaire (dispatch UI : Owner voit tous les outils avancés, autres voient un rapport lecture seule).
  // 📏 [v13.63 Phase 5] Colonnes kilométrage — visibles seulement pour les rôles
  // détenant la clé `paie_voir_kilometrage` (Owner short-circuit).
  const { canDo } = usePermissions() || {};
  const canViewKilometrage = !!(isOwner || (canDo && canDo('paie_voir_kilometrage')));
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const today = now.toISOString().split('T')[0];

  const [dateDebut, setDateDebut] = useState(firstOfMonth);
  const [dateFin, setDateFin] = useState(today);
  const [employeeId, setEmployeeId] = useState('all');
  const [employees, setEmployees] = useState([]);
  // 🆕 [v13.50 — Item 3] Filtre par bon de travail : quand un BT est sélectionné,
  // le serveur ne retourne que les punchs de ce BT. Combiné avec les autres filtres.
  const [filtreBtId, setFiltreBtId] = useState('');
  // 🆕 [v13.50 — Item 2] Recherche libre : filtre côté client sur la liste des
  // punchs déjà chargés. Cherche dans : notes_validation (note owner), note_employe
  // (dernière demande employé), employee_nom, client_nom, nom_dossier, numero_bt.
  const [filtreTexte, setFiltreTexte] = useState('');
  // 🆕 [v13.50 — Item 4] Dialog historique conversationnel employé ⇄ propriétaire.
  const [historiquePunch, setHistoriquePunch] = useState(null); // {id, label}
  // 🆕 [v13.51] Filtre par période de paie : consomme /api/paie/periodes (source
  // unique de vérité — ancre officielle backend). Quand une période est
  // sélectionnée, les inputs date deviennent grisés + non modifiables.
  const [periodesPaye, setPeriodesPaye] = useState([]);           // liste des périodes disponibles
  const [periodeCouranteNum, setPeriodeCouranteNum] = useState(null);
  const [periodeSelectionnee, setPeriodeSelectionnee] = useState(''); // "" | "16"
  const [rapport, setRapport] = useState(null);
  const [loading, setLoading] = useState(false);
  // 📏 [v13.63 Phase 5] Kilométrage par employé — map { employee_id → { total_payable_km, total_montant_dollars, jours } }
  //     Chargé après chaque `search()` réussie, uniquement pour les rôles habilités.
  const [kmParEmploye, setKmParEmploye] = useState({});
  const [kmTotaux, setKmTotaux] = useState({ total_payable_km: 0, total_montant_dollars: 0 });
  const [kmLoading, setKmLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [searched, setSearched] = useState(false);
  const [alertes, setAlertes] = useState([]);
  // 🆕 [5.6] Drawer "Détails du punch" — affiche TOUTES les infos disponibles
  const [drawerPunch, setDrawerPunch] = useState(null);

  // 🆕 [Phase 49] Totaux d'heures nettes par employé + date locale (jour calendaire
  // QC). Permet d'afficher le « Total jour » à droite des heures par punch.
  // Clé : `${employee_id}_${YYYY-MM-DD}` (date du punch_in_time)
  const totauxParJour = useMemo(() => {
    const acc = {};
    (rapport?.punches || []).forEach(p => {
      if (!p?.employee_id || !p?.punch_in_time) return;
      const date = p.punch_in_time.slice(0, 10);
      const key = `${p.employee_id}_${date}`;
      acc[key] = (acc[key] || 0) + (parseFloat(p.heures_nettes) || 0);
    });
    return acc;
  }, [rapport]);
  const SEUIL_TOTAL_JOUR_H = 7 + 25 / 60;  // 7h25 = seuil effectif Phase 49

  // 🆕 Segmentation d'un punch en 2 BT
  const [splitPunch, setSplitPunch] = useState(null);
  const [splitNewBtId, setSplitNewBtId] = useState("");
  const [splitNotes, setSplitNotes] = useState("");
  const [splitLoading, setSplitLoading] = useState(false);
  // 🆕 2026-05-08 — Mode du dialog : 'assign' | 'edit' | 'split'
  const [splitMode, setSplitMode] = useState('assign');
  // 🆕 2026-05-08 — Mode "Modifier" : édition complète d'un punch existant
  const [editForm, setEditForm] = useState({ punch_in: '', punch_out: '', pause_min: 0, bt_id: '' });
  // 🆕 2026-05-08 — Mode "Segmenter" v2 : 2 segments avec heures/pause/BT indépendants
  const [splitSegments, setSplitSegments] = useState([
    { punch_in: '', punch_out: '', pause_min: 0, bt_id: '' },
    { punch_in: '', punch_out: '', pause_min: 0, bt_id: '' },
  ]);
  // 🆕 2026-05-08 — Journal d'audit du punch courant
  const [auditLog, setAuditLog] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  const loadAuditLog = async (punchId) => {
    if (!punchId) return;
    setAuditLoading(true);
    try {
      const res = await axios.get(`${api.baseUrl}/punch/audit-log?punch_id=${punchId}&limit=50`);
      setAuditLog(res.data?.logs || []);
    } catch {
      setAuditLog([]);
    } finally {
      setAuditLoading(false);
    }
  };

  // 🆕 2026-05-08 — Rattrapage des pointages Admin déjà associés à un BT
  const [backfillLoading, setBackfillLoading] = useState(false);
  // 🆕 2026-05-08 — Filtre « punchs à risque »
  const [riskFilter, setRiskFilter] = useState(false);

  // 🆕 [Phase 80.2 — 2026-06-22] Toggle "Inclure punchs test" (par défaut OFF)
  // + state pour le dialog de suppression bulk des test punches
  const [includeTestPunches, setIncludeTestPunches] = useState(false);
  const [showBulkDeleteTestDialog, setShowBulkDeleteTestDialog] = useState(false);
  const [bulkDeletingTest, setBulkDeletingTest] = useState(false);

  // 🆕 2026-05-11 — Cash payment dialog
  // 🆕 Phase 105 (2026-07-09) — Enrichi avec prime horaire ponctuelle
  const [cashDialog, setCashDialog] = useState(null); // {punch}
  const [cashHeures, setCashHeures] = useState('');
  const [cashTaux, setCashTaux] = useState('');
  const [cashNote, setCashNote] = useState('');
  const [cashLoading, setCashLoading] = useState(false);
  // Phase 105 — États prime
  const [cashType, setCashType] = useState('simple'); // 'simple' | 'avec_prime'
  const [primeHoraire, setPrimeHoraire] = useState('');
  const [primeModePaiement, setPrimeModePaiement] = useState('cash'); // 'cash' | 'cheque'
  const [primeEstDeclaree, setPrimeEstDeclaree] = useState(false);
  const [primeDescription, setPrimeDescription] = useState('');

  useEffect(() => {
    if (cashDialog?.punch) {
      const p = cashDialog.punch;
      const restantHeures = Math.max(0, (p.heures_nettes || 0) - (p.heures_cash || 0));
      setCashHeures(String(restantHeures.toFixed(2)));
      setCashTaux('');
      setCashNote('');
      // Phase 105 — reset prime
      setCashType('simple');
      setPrimeHoraire('');
      setPrimeModePaiement('cash');
      setPrimeEstDeclaree(false);
      setPrimeDescription('');
    }
  }, [cashDialog]);

  // 🆕 Phase 105 — Auto-réinitialisation de est_declaree si mode passe à cash (combinaison interdite)
  useEffect(() => {
    if (primeModePaiement === 'cash' && primeEstDeclaree) {
      setPrimeEstDeclaree(false);
    }
  }, [primeModePaiement, primeEstDeclaree]);

  const handleAddCashPayment = async () => {
    if (!cashDialog?.punch) return;
    const heures = parseFloat(String(cashHeures).replace(',', '.'));
    const taux = parseFloat(String(cashTaux).replace(',', '.'));
    if (!heures || heures <= 0) { toast.error('Heures cash requises'); return; }
    if (!taux || taux <= 0) { toast.error('Taux horaire cash requis'); return; }

    // Phase 105 — Validation prime
    const payload = { heures, taux_horaire: taux, note: cashNote };
    if (cashType === 'avec_prime') {
      const primeH = parseFloat(String(primeHoraire).replace(',', '.'));
      if (!primeH || primeH <= 0) { toast.error('Prime horaire requise (> 0)'); return; }
      if (primeModePaiement === 'cash' && primeEstDeclaree) {
        toast.error('Une prime cash ne peut pas être déclarée (combinaison interdite).');
        return;
      }
      payload.prime_horaire = primeH;
      payload.prime_mode_paiement = primeModePaiement;
      payload.prime_est_declaree = primeEstDeclaree;
      payload.prime_description = primeDescription;
    }

    setCashLoading(true);
    try {
      await axios.post(`${api.baseUrl}/punch/${cashDialog.punch.id}/cash-payment`, payload);
      const totalMsg = cashType === 'avec_prime'
        ? `${heures.toFixed(2)}h × ${taux.toFixed(2)}$ + prime ${parseFloat(String(primeHoraire).replace(',','.')).toFixed(2)}$/h enregistrés`
        : `${heures.toFixed(2)}h × ${taux.toFixed(2)}$ = ${(heures * taux).toFixed(2)}$ payé cash`;
      toast.success(totalMsg);
      setCashDialog(null);
      search();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setCashLoading(false);
    }
  };

  const handleRemoveCashPayment = async (punchId, cashId) => {
    if (!isOwner) return;
    setCashLoading(true);
    try {
      await axios.delete(`${api.baseUrl}/punch/${punchId}/cash-payment/${cashId}`);
      toast.success('Paiement cash retiré');
      setCashDialog(null);
      search();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setCashLoading(false);
    }
  };

  /** Détecte si un punch validé mérite une revue manuelle (paie/coût client). */
  const isPunchAtRisk = (p) => {
    if (!p || p.type_punch === 'ferie_cnesst') return false;
    const reasons = [];
    // 🆕 2026-05-08 — Critère « Admin sans BT » retiré : c'est un cas normal
    // (proprio/CP qui fait du travail admin, pas un risque).
    const h = Number(p.heures_nettes || 0);
    if (h > 0 && (h < 1 || h > 12)) reasons.push('duree');
    const hasGps = !!(p.punch_in_latitude || p.punch_out_latitude || p.latitude);
    if (!hasGps && p.type_punch !== 'admin') reasons.push('gps');
    return reasons.length > 0 ? reasons : false;
  };
  const runBackfillAdminBT = async () => {
    if (!window.confirm('Convertir tous les pointages Admin déjà associés à un BT en pointages "chantier" ? Cette action est idempotente et réinjecte les heures dans les stats des BT.')) return;
    setBackfillLoading(true);
    try {
      const res = await axios.post(`${api.baseUrl}/punch/backfill-admin-with-bt`);
      const d = res.data || {};
      toast.success(`Rattrapage effectué : ${d.converted || 0} punch(s) converti(s), ${d.bts_updated || 0} BT(s) mis à jour, ${d.heures_total_redistribuees || 0}h redistribuées.`);
      if (searched) search();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur rattrapage');
    } finally {
      setBackfillLoading(false);
    }
  };

  // ── CNESST Jours fériés ────────────────────────────────
  const [feriesConfig, setFeriesConfig] = useState({ paques_mode: 'lundi_paques' });
  const [savingPaquesMode, setSavingPaquesMode] = useState(false);

  const fetchFeriesConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${api.baseUrl}/jours-feries/config`);
      setFeriesConfig(r.data || { paques_mode: 'lundi_paques' });
    } catch { /* silencieux */ }
  }, [api.baseUrl]);

  const savePaquesMode = async (mode) => {
    setSavingPaquesMode(true);
    try {
      await axios.put(`${api.baseUrl}/jours-feries/config`, { paques_mode: mode });
      setFeriesConfig({ paques_mode: mode });
      toast.success(`Mode Pâques mis à jour : ${mode === 'vendredi_saint' ? 'Vendredi saint' : 'Lundi de Pâques'}`);
    } catch { toast.error('Erreur sauvegarde mode Pâques'); }
    finally { setSavingPaquesMode(false); }
  };

  const fetchAlertes = useCallback(async () => {
    try {
      const res = await axios.get(`${api.baseUrl}/alertes-paie`);
      if (res.status !== 200) return;
      const data = res.data;
      setAlertes(data.alertes || []);
    } catch { /* silencieux */ }
  }, [api.baseUrl]);

  // 🆕 [v13.51] Charge les 26 dernières périodes de paie depuis l'endpoint canonique.
  // Source unique de vérité — respecte l'ancre officielle définie dans
  // `backend/routers/contrats.py`. Aucun recalcul JS local.
  const fetchPeriodesPaye = useCallback(async () => {
    try {
      const res = await axios.get(`${api.baseUrl}/paie/periodes?n=26`);
      if (res.status !== 200) return;
      const d = res.data || {};
      setPeriodesPaye(d.periodes || []);
      setPeriodeCouranteNum(d.periode_courante?.numero ?? null);
    } catch { /* silencieux — filtre optionnel */ }
  }, [api.baseUrl]);

  useEffect(() => {
    // 🆕 2026-05-14 — `include_owners=true` pour que les propriétaires sans
    //   fiche employé (ex. Éliot Savard) apparaissent dans le dropdown du rapport
    //   AVEC le même id que celui utilisé à l'import (sinon les heures importées
    //   ne se retrouvaient pas filtrables par leur nom).
    axios.get(`${api.baseUrl}/employees?include_owners=true&include_inactive=true`).then(r => r.data).then(d => setEmployees(d.employees || d || []));
    fetchAlertes();
    fetchFeriesConfig();
    fetchPeriodesPaye();
  }, [api.baseUrl, fetchAlertes, fetchFeriesConfig, fetchPeriodesPaye]);

  const handleHandleAlerte = async (alerteId) => {
    try {
      await axios.post(`${api.baseUrl}/alertes-paie/${alerteId}/handle`);
      toast.success('Alerte marquée comme traitée');
      setAlertes(prev => prev.filter(a => a.id !== alerteId));
    } catch { toast.error('Erreur'); }
  };

  const handleDismissAlerte = async (alerteId) => {
    try {
      await axios.post(`${api.baseUrl}/alertes-paie/${alerteId}/dismiss`);
      toast.info('Alerte ignorée');
      setAlertes(prev => prev.filter(a => a.id !== alerteId));
    } catch { toast.error('Erreur'); }
  };

  // 🆕 [v13.51] Helper de format court FR-CA d'une date ISO ("2026-07-12" → "12 juil").
  // Utilise Intl.DateTimeFormat pour éviter tout parsing manuel.
  const fmtDateCourt = (iso) => {
    if (!iso) return '';
    try {
      // Ajouter T12:00 pour éviter les décalages timezone sur les dates pures
      const d = new Date(iso + 'T12:00:00');
      if (isNaN(d)) return String(iso);
      return d.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' })
        .replace(/\.$/, '');  // retire le "." final sur les mois abrégés (« juil. » → « juil »)
    } catch {
      return String(iso);
    }
  };

  // 🆕 [v13.51] Label d'une période pour le Select — format « P16 · 12 juil au 25 juil ».
  // La période courante reçoit un emoji 🎯 en fin de libellé.
  const labelPeriode = (p) => {
    if (!p) return '';
    const debut = fmtDateCourt(p.debut);
    const fin = fmtDateCourt(p.fin);
    const badge = periodeCouranteNum !== null && p.numero === periodeCouranteNum ? ' 🎯 Actuelle' : '';
    return `P${p.numero} · ${debut} au ${fin}${badge}`;
  };

  // 🆕 [v13.51] Sélection d'une période → remplit dateDebut/dateFin, désactive les
  // inputs date (via `periodeSelectionnee` non vide), et auto-lance la recherche.
  const handleSelectPeriode = (numStr) => {
    setPeriodeSelectionnee(numStr);
    if (!numStr) return;  // « Aucune » → laisse les dates modifiables telles quelles
    const num = parseInt(numStr, 10);
    const p = periodesPaye.find(x => x.numero === num);
    if (!p) return;
    setDateDebut(p.debut);
    setDateFin(p.fin);
    // Auto-lance la recherche : on utilise directement les dates trouvées pour éviter
    // le déphasage state React (setState est asynchrone).
    setTimeout(() => search({ dateDebutOverride: p.debut, dateFinOverride: p.fin }), 0);
  };

  const handleEffacerPeriode = () => {
    setPeriodeSelectionnee('');
  };

  const periodeActive = !!periodeSelectionnee;

  const search = async (opts = {}) => {
    setLoading(true);
    try {
      const dDebut = opts.dateDebutOverride || dateDebut;
      const dFin = opts.dateFinOverride || dateFin;
      const params = new URLSearchParams({ date_debut: dDebut, date_fin: dFin });
      if (employeeId !== 'all') params.set('employee_id', employeeId);
      // 🆕 [v13.50 — Item 3] Filtre serveur par bon de travail
      if (filtreBtId) params.set('bon_travail_id', filtreBtId);
      const res = await axios.get(`${api.baseUrl}/punch/rapport?${params}`);
      const data = res.data;
      setRapport(data);
      setSearched(true);
      // 📏 [v13.63 Phase 5] Charge le kilométrage figé (snapshots) pour chaque employé
      //     présent dans le rapport, uniquement si l'utilisateur peut voir la colonne.
      if (canViewKilometrage) {
        fetchKilometrageParEmploye(data?.par_employe || [], dDebut, dFin);
      } else {
        setKmParEmploye({});
        setKmTotaux({ total_payable_km: 0, total_montant_dollars: 0 });
      }
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  // 📏 [v13.63 Phase 5] Récupère les snapshots kilométrage figés pour chaque employé
  //     du rapport. Appel `/api/kilometrage/rapport-range/{id}?debut&fin` en parallèle.
  //     Un rejet 403/404 par employé est neutre (silencieux) — l'employé n'a juste pas
  //     de snapshot pour la période. Aucun recalcul rétroactif (backend read-only).
  const fetchKilometrageParEmploye = useCallback(async (employes, debut, fin) => {
    if (!employes.length) {
      setKmParEmploye({}); setKmTotaux({ total_payable_km: 0, total_montant_dollars: 0 });
      return;
    }
    setKmLoading(true);
    try {
      const results = await Promise.allSettled(
        employes.map(e =>
          axios.get(`${api.baseUrl}/kilometrage/rapport-range/${e.employee_id}`, {
            params: { debut, fin },
          }),
        ),
      );
      const map = {};
      let totKm = 0;
      let totMnt = 0;
      results.forEach((r, i) => {
        if (r.status === 'fulfilled' && r.value?.data) {
          const empId = employes[i].employee_id;
          map[empId] = r.value.data;
          totKm += (r.value.data.total_payable_km || 0);
          totMnt += (r.value.data.total_montant_dollars || 0);
        }
      });
      setKmParEmploye(map);
      setKmTotaux({
        total_payable_km: Math.round(totKm * 100) / 100,
        total_montant_dollars: Math.round(totMnt * 100) / 100,
      });
    } finally { setKmLoading(false); }
  }, [api.baseUrl]);

  const exportCSV = () => {
    if (!rapport?.punches?.length) return;
    // 🚚 [Phase v13.52] Colonnes Temps déplacement + Total rémunéré exportées.
    const headers = ['Date', 'Employé', 'Punch IN', 'Punch OUT', 'Heures nettes', 'Temps déplacement (min)', 'Total rémunéré (h)', 'Type', 'Bon de travail', 'Pause'];
    const rows = rapport.punches.map(p => {
      const tdMin = parseInt(p.temps_deplacement_minutes, 10) || 0;
      const tdH = tdMin / 60;
      return [
        p.punch_in_time?.slice(0, 10),
        p.employee_nom || p.employee_id,
        fmtTime(p.punch_in_time),
        p.punch_out_time ? fmtTime(p.punch_out_time) : 'En cours',
        (p.heures_nettes || 0).toFixed(2),
        tdMin,
        ((p.heures_nettes || 0) + tdH).toFixed(2),
        p.type_punch || 'chantier',
        p.bon_travail_id || '',
        (p.heures_pause || 0).toFixed(2),
      ];
    });
    const csv = [headers, ...rows].map(r => r.join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport_pointage_${dateDebut}_${dateFin}.csv`;
    a.click();
  };

  const handleDeletePunch = async (punchId) => {
    setDeletingId(punchId);
    try {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const res = await axios.delete(`${api.baseUrl}/punch/${punchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 200) {
        toast.success('Pointage supprimé');
        setConfirmDeleteId(null);
        search(); // Rafraîchir les données
      } else {
        toast.error(res.data?.detail || 'Erreur suppression');
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setDeletingId(null); }
  };

  // 🆕 [Phase 80.2 — 2026-06-22] Bulk delete tous les punchs test (Owner-only)
  // Appelle DELETE /api/debug/punch-pause-pm-test/all
  const handleBulkDeleteTestPunches = async () => {
    if (!isOwner) return;
    setBulkDeletingTest(true);
    try {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const res = await axios.delete(`${api.baseUrl}/debug/punch-pause-pm-test/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const n = res.data?.deleted_count || 0;
      toast.success(`🗑️ ${n} punch${n > 1 ? 's' : ''} test supprimé${n > 1 ? 's' : ''}`);
      setShowBulkDeleteTestDialog(false);
      search();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression bulk');
    } finally {
      setBulkDeletingTest(false);
    }
  };

  // 🆕 Helpers pour la segmentation flexible
  const toTimeStr = (iso) => {
    try {
      const d = new Date(iso);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    } catch { return ''; }
  };
  const composeISO = (datePart, timeStr) => {
    // datePart: "YYYY-MM-DD", timeStr: "HH:MM" → ISO local
    if (!datePart || !timeStr) return '';
    return new Date(`${datePart}T${timeStr}:00`).toISOString();
  };

  // 🆕 Ouvrir le dialog (segmentation / édition / réassignation)
  const openSplitDialog = (punch) => {
    setSplitPunch(punch);
    setShowAudit(false);
    setAuditLog(null);
    // Mode par défaut : 'assign' si le punch n'a pas de BT, sinon 'split'
    setSplitMode(punch.bon_travail_id ? 'split' : 'assign');
    setSplitNewBtId('');
    setSplitNotes('');

    // Pré-remplir le formulaire "Modifier" avec les valeurs actuelles
    const pauseMinCurrent = Math.round((Number(punch.heures_pause) || 0) * 60);
    setEditForm({
      punch_in: toTimeStr(punch.punch_in_time),
      punch_out: toTimeStr(punch.punch_out_time),
      pause_min: pauseMinCurrent,
      bt_id: punch.bon_travail_id || '',
    });

    // Pré-remplir les 2 segments : moitié-moitié, mêmes pauses ventilées
    const tIn = toTimeStr(punch.punch_in_time);
    const tOut = toTimeStr(punch.punch_out_time);
    let mid = '12:00';
    try {
      const inMs = new Date(punch.punch_in_time).getTime();
      const outMs = new Date(punch.punch_out_time).getTime();
      const m = new Date((inMs + outMs) / 2);
      mid = `${String(m.getHours()).padStart(2, '0')}:${String(m.getMinutes()).padStart(2, '0')}`;
    } catch {/* fallback 12:00 */}
    const halfPause = Math.round(pauseMinCurrent / 2);
    setSplitSegments([
      // 🐛 2026-05-12 — Les DEUX segments défaut sur le BT actuel du punch
      // (cas le plus courant : segmenter en 2 BT chantier différents).
      // L'utilisateur peut effacer un BT pour le marquer comme heures admin.
      { punch_in: tIn, punch_out: mid, pause_min: halfPause, bt_id: punch.bon_travail_id || '' },
      { punch_in: mid, punch_out: tOut, pause_min: pauseMinCurrent - halfPause, bt_id: punch.bon_travail_id || '' },
    ]);
  };

  const updateSegment = (idx, field, value) => {
    setSplitSegments(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const submitSplit = async () => {
    if (!splitPunch) return;
    setSplitLoading(true);
    try {
      const datePart = splitPunch.punch_in_time.slice(0, 10);

      if (splitMode === 'assign') {
        if (!splitNewBtId) { toast.error('BT requis'); setSplitLoading(false); return; }
        const payload = {
          bon_travail_id: splitNewBtId,
          ...(splitNotes ? { notes_validation: splitNotes } : {}),
        };
        const res = await axios.put(`${api.baseUrl}/punch/${splitPunch.id}/adjust`, payload);
        if (res.status === 200) {
          toast.success(splitPunch.bon_travail_id ? 'Punch réassigné au nouveau BT' : 'Punch assigné au BT');
          setSplitPunch(null);
          search();
        }
      } else if (splitMode === 'edit') {
        // 🆕 Mode "Modifier" : ajuster heures + pause + BT en une fois via /adjust
        if (!editForm.punch_in || !editForm.punch_out) { toast.error('Heures début et fin requises'); setSplitLoading(false); return; }
        const payload = {
          punch_in_time: composeISO(datePart, editForm.punch_in),
          punch_out_time: composeISO(datePart, editForm.punch_out),
          heures_pause: (Number(editForm.pause_min) || 0) / 60,
          bon_travail_id: editForm.bt_id || null,
          valide: true,
          ...(splitNotes ? { notes_validation: splitNotes } : {}),
        };
        const res = await axios.put(`${api.baseUrl}/punch/${splitPunch.id}/adjust`, payload);
        if (res.status === 200) {
          toast.success('Punch modifié avec succès');
          setSplitPunch(null);
          search();
        }
      } else {
        // 🆕 Mode "Segmenter" v2 : 2 segments indépendants → /split-flex
        const segs = splitSegments;
        for (let i = 0; i < segs.length; i++) {
          if (!segs[i].punch_in || !segs[i].punch_out) {
            toast.error(`Segment ${i + 1} : heures de début et fin requises`); setSplitLoading(false); return;
          }
        }
        const payload = {
          segments: segs.map(s => ({
            punch_in_time: composeISO(datePart, s.punch_in),
            punch_out_time: composeISO(datePart, s.punch_out),
            heures_pause_minutes: Number(s.pause_min) || 0,
            bon_travail_id: s.bt_id || null,
          })),
          global_notes: splitNotes || '',
        };
        const res = await axios.post(`${api.baseUrl}/punch/${splitPunch.id}/split-flex`, payload);
        if (res.status === 200) {
          toast.success('Punch segmenté en 2');
          setSplitPunch(null);
          search();
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSplitLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart2 className="w-8 h-8 text-blue-600" /> Rapport de Pointage
          </h1>
          <p className="text-slate-500 mt-1">Consultez et exportez toutes les heures validées par période et par employé.</p>
        </div>
        {isOwner && (
          <Button
            variant="outline"
            size="sm"
            onClick={runBackfillAdminBT}
            disabled={backfillLoading}
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
            title="Rattrape les pointages Admin déjà associés à un BT (avant le fix du 2026-05-08) : convertit type_punch admin → chantier et ré-incrémente les heures réelles des BT."
            data-testid="btn-backfill-admin-bt"
          >
            <Scissors className="w-3.5 h-3.5 mr-1.5" />
            {backfillLoading ? 'Rattrapage…' : 'Rattraper pointages Admin → BT'}
          </Button>
        )}
      </div>

      {/* Alertes 0h assurances */}
      {alertes.length > 0 && (
        <div className="rounded-lg border border-red-300 bg-red-50 overflow-hidden" data-testid="alertes-zero-heure">
          <div className="flex items-center gap-2 px-4 py-3 bg-red-100 border-b border-red-200">
            <Bell className="w-5 h-5 text-red-600 shrink-0" />
            <span className="font-semibold text-red-800 text-sm">
              {alertes.length} alerte{alertes.length > 1 ? 's' : ''} — Employé{alertes.length > 1 ? 's' : ''} à 0h avec assurance collective
            </span>
            <button
              onClick={fetchAlertes}
              className="ml-auto text-red-400 hover:text-red-700"
              title="Actualiser"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <div className="divide-y divide-red-100">
            {alertes.map((alerte) => (
              <div key={alerte.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`alerte-${alerte.id}`}>
                <div className="flex-1">
                  <p className="font-semibold text-red-900 text-sm">{alerte.employee_nom}</p>
                  <p className="text-xs text-red-700 mt-0.5">
                    Période {alerte.periode_debut} → {alerte.periode_fin} · 0h travaillées
                  </p>
                  <p className="text-xs text-red-600 mt-0.5">
                    Cotisation assurance: {alerte.assurance_h?.toFixed(4)} $/h · Déduction manquante: <strong>{alerte.deduction_manquante?.toFixed(2)} $</strong>
                  </p>
                  <p className="text-xs text-amber-700 mt-1 font-medium">
                    Action : Doubler la déduction d'assurance sur la prochaine paie
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => handleHandleAlerte(alerte.id)}
                    className="inline-flex items-center gap-1 text-xs font-medium bg-green-100 hover:bg-green-200 text-green-800 px-3 py-1.5 rounded-lg transition-colors"
                    data-testid={`btn-handle-alerte-${alerte.id}`}
                    title="Marquer comme traité"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    Traité
                  </button>
                  <button
                    onClick={() => handleDismissAlerte(alerte.id)}
                    className="inline-flex items-center gap-1 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-600 px-2 py-1.5 rounded-lg transition-colors"
                    data-testid={`btn-dismiss-alerte-${alerte.id}`}
                    title="Ignorer"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CNESST — Jours Fériés (affichage uniquement si la période sélectionnée touche Pâques, ou si au moins un punch férié est présent) */}
      {isOwner && (rangeOverlapsPaques(dateDebut, dateFin) || (rapport?.punches || []).some(p => p.type_punch === 'ferie_cnesst' && /P[aâ]ques|saint/i.test(p.nom_ferie || ''))) && (
        <Card className="border-yellow-300 bg-yellow-50/50" data-testid="card-feries-cnesst">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-yellow-900">
              <Calendar className="w-5 h-5" /> Jours Fériés CNESST — Choix Pâques
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <Label className="text-xs text-slate-600">Mode Pâques par défaut (employeur)</Label>
                <Select
                  value={feriesConfig.paques_mode}
                  onValueChange={savePaquesMode}
                  disabled={savingPaquesMode}
                >
                  <SelectTrigger className="w-56 mt-1" data-testid="select-paques-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lundi_paques">Lundi de Pâques</SelectItem>
                    <SelectItem value="vendredi_saint">Vendredi saint</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500 mt-1">
                  Règle intelligente par employé : si l'employé a travaillé un de ces deux jours, le férié retenu est l'autre.
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-600 mt-3 italic">
              ✨ Les indemnités CNESST (règle 1/20 sur les 4 semaines précédentes, hors heures sup) sont calculées automatiquement chaque jour. Aucune action manuelle requise.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Filtres */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <Label className="text-xs text-slate-500">Date de début</Label>
              <Input
                type="date"
                value={dateDebut}
                onChange={e => setDateDebut(e.target.value)}
                className={`w-40 mt-1 ${periodeActive ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}`}
                disabled={periodeActive}
                data-testid="input-date-debut"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Date de fin</Label>
              <Input
                type="date"
                value={dateFin}
                onChange={e => setDateFin(e.target.value)}
                className={`w-40 mt-1 ${periodeActive ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}`}
                disabled={periodeActive}
                data-testid="input-date-fin"
              />
            </div>
            {/* 🆕 [v13.51] Filtre par période de paie — bi-hebdomadaire, ancre backend.
                Sélection → remplit dateDebut/dateFin, désactive les inputs date, auto-lance. */}
            <div>
              <Label className="text-xs text-slate-500">Période de paie</Label>
              <div className="flex items-center gap-1 mt-1">
                <select
                  value={periodeSelectionnee}
                  onChange={(e) => handleSelectPeriode(e.target.value)}
                  className="border border-slate-200 rounded-md px-3 py-1.5 text-sm bg-white w-64"
                  data-testid="select-periode-paye"
                  aria-label="Filtrer par période de paie"
                >
                  <option value="">— Aucune —</option>
                  {periodesPaye.slice().sort((a, b) => b.numero - a.numero).map(p => (
                    <option
                      key={p.numero}
                      value={String(p.numero)}
                      data-testid={`option-periode-${p.numero}`}
                    >
                      {labelPeriode(p)}
                    </option>
                  ))}
                </select>
                {periodeActive && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleEffacerPeriode}
                    className="text-xs"
                    data-testid="btn-effacer-periode-paye"
                    title="Effacer le filtre par période"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Employé</Label>
              <div className="flex items-center gap-1 mt-1">
                {/* 🆕 [Phase 31 #1] EmployeeSelector — inclut les inactifs (ex. Audrey Girard) */}
                <div className="w-56">
                  <EmployeeSelector
                    employees={employees}
                    value={employeeId === 'all' ? '' : employeeId}
                    onChange={(id) => setEmployeeId(id || 'all')}
                    includeInactive={true}
                    placeholder={employeeId === 'all' ? 'Tous les employés' : 'Rechercher un employé…'}
                    data-testid="rapport-heures-employee-selector"
                  />
                </div>
                {employeeId !== 'all' && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setEmployeeId('all')}
                    className="text-xs"
                    data-testid="btn-clear-employee-filter"
                    title="Voir tous les employés"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
            <Button onClick={search} disabled={loading} className="mt-1" data-testid="btn-rechercher-rapport">
              <Search className="w-4 h-4 mr-1" />
              {loading ? 'Recherche...' : 'Rechercher'}
            </Button>
            {rapport && (
              <Button variant="outline" onClick={exportCSV} className="mt-1">
                <Download className="w-4 h-4 mr-1" /> Exporter CSV
              </Button>
            )}
          </div>

          {/* 🆕 [v13.50 — Items 2 + 3] Filtre par bon de travail + recherche libre.
              Le filtre BT est appliqué côté serveur (au clic sur « Rechercher »).
              La recherche libre est appliquée côté client sur la liste chargée. */}
          <div className="flex flex-wrap gap-4 items-end mt-4 pt-4 border-t border-slate-100">
            <div className="w-full sm:w-96">
              <Label className="text-xs text-slate-500">Filtrer par bon de travail (optionnel)</Label>
              <div className="mt-1">
                <BtSearchCombobox
                  value={filtreBtId}
                  onChange={(id) => setFiltreBtId(id || '')}
                  placeholder="🔍 Choisir un bon de travail…"
                  testIdPrefix="rapport-filtre-bt"
                />
              </div>
            </div>
            <div className="w-full sm:w-72">
              <Label className="text-xs text-slate-500">Recherche libre (note, employé, projet)</Label>
              <div className="mt-1 relative">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="text"
                  value={filtreTexte}
                  onChange={(e) => setFiltreTexte(e.target.value)}
                  placeholder="Ex. : « oublié pause », nom client…"
                  className="pl-8"
                  data-testid="input-recherche-libre-rapport"
                />
                {filtreTexte && (
                  <button
                    type="button"
                    onClick={() => setFiltreTexte('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    title="Effacer"
                    data-testid="btn-clear-recherche-libre"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Résultats */}
      {searched && rapport && (
        <>
          {/* Totaux */}
          {/* 🆕 [v13.50 — Item 3] Compteur BT : quand un BT est sélectionné, on
              met en évidence le nombre de punchs et les heures totales pour ce BT
              afin de rendre la réponse instantanément lisible. */}
          {filtreBtId && (
            <Card className="bg-indigo-50 border-indigo-300" data-testid="rapport-compteur-bt">
              <CardContent className="pt-3 pb-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-indigo-700 font-semibold">
                    Filtre bon de travail actif
                  </p>
                  <p className="text-sm text-indigo-900 mt-0.5">
                    <span className="font-bold" data-testid="compteur-bt-punchs">{rapport.total}</span> punch{rapport.total > 1 ? 's' : ''}
                    <span className="mx-2 text-indigo-400">•</span>
                    <span className="font-bold" data-testid="compteur-bt-heures">{fmtH(rapport.total_heures_nettes)}</span> pour ce bon de travail
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { setFiltreBtId(''); setTimeout(search, 0); }}
                  className="text-xs font-medium bg-white text-indigo-700 hover:bg-indigo-100 border border-indigo-300 px-3 py-1.5 rounded-md transition-colors"
                  data-testid="btn-clear-filtre-bt"
                >
                  <X className="w-3.5 h-3.5 inline mr-1" /> Retirer le filtre
                </button>
              </CardContent>
            </Card>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4 pb-3 text-center">
                <p className="text-2xl font-bold text-blue-700">{rapport.total}</p>
                <p className="text-xs text-blue-600">Pointages</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-4 pb-3 text-center">
                <p className="text-2xl font-bold text-green-700">{fmtH(rapport.total_heures_nettes)}</p>
                <p className="text-xs text-green-600">Heures nettes totales</p>
                {(rapport.total_heures_cash || 0) > 0 && (
                  <p className="text-[10px] text-emerald-700 font-semibold mt-0.5">
                    dont {fmtH(rapport.total_heures_cash)} payées cash
                  </p>
                )}
                {/* 🚚 [Phase v13.52] Décomposition déplacement */}
                {(rapport.total_temps_deplacement_minutes || 0) > 0 && (
                  <p className="text-[10px] text-sky-700 font-semibold mt-0.5" data-testid="total-temps-deplacement">
                    + {fmtH(rapport.total_heures_deplacement)} déplacement
                    <span className="text-slate-400 font-normal ml-1">
                      = {fmtH(rapport.total_heures_remunerees)} rémunérées
                    </span>
                  </p>
                )}
              </CardContent>
            </Card>
            {(rapport.total_heures_cash || 0) > 0 && (
              <Card className="bg-emerald-50 border-emerald-200">
                <CardContent className="pt-4 pb-3 text-center">
                  <p className="text-2xl font-bold text-emerald-700">{(rapport.total_montant_cash || 0).toFixed(2)} $</p>
                  <p className="text-xs text-emerald-600">À payer en argent</p>
                  <p className="text-[10px] text-emerald-600 mt-0.5">
                    Normal : {fmtH(rapport.total_heures_normales)} · Cash : {fmtH(rapport.total_heures_cash)}
                  </p>
                  {(rapport.total_prime_ponctuelle || 0) > 0 && (
                    <p className="text-[10px] text-amber-700 font-semibold mt-0.5" data-testid="total-prime-ponctuelle">
                      + Primes ponctuelles : {(rapport.total_prime_ponctuelle).toFixed(2)} $
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="pt-4 pb-3 text-center">
                <p className="text-2xl font-bold text-purple-700">{rapport.par_employe?.length || 0}</p>
                <p className="text-xs text-purple-600">Employés actifs</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-4 pb-3 text-center">
                <p className="text-2xl font-bold text-amber-700">
                  {rapport.total > 0 ? fmtH(rapport.total_heures_nettes / (rapport.par_employe?.length || 1)) : '0h00'}
                </p>
                <p className="text-xs text-amber-600">Moy./employé</p>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50 border-yellow-300">
              <CardContent className="pt-4 pb-3 text-center">
                <p className="text-2xl font-bold text-yellow-700">
                  {(rapport.total_indemnites_feries || 0).toFixed(2)} $
                </p>
                <p className="text-xs text-yellow-700">Indemnités fériés (CNESST)</p>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="detail">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <TabsList>
                <TabsTrigger value="detail">Détail ({rapport.total})</TabsTrigger>
                <TabsTrigger value="par_employe">Par employé ({rapport.par_employe?.length})</TabsTrigger>
              </TabsList>
              <div className="flex items-center gap-2 flex-wrap">
                {/* 🆕 [Phase 80.2 — 2026-06-22] Toggle "Inclure punchs test" + bulk delete (Owner-only)
                    Le toggle masque par défaut les punches `is_test_punch=true`.
                    Le bouton bulk supprime TOUS les punchs test (avec confirmation). */}
                {isOwner && (() => {
                  const testCount = (rapport.punches || []).filter(p => p.is_test_punch === true).length;
                  return (
                    <>
                      <button
                        type="button"
                        onClick={() => setIncludeTestPunches(v => !v)}
                        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${includeTestPunches ? 'bg-orange-600 text-white hover:bg-orange-700' : 'bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200'}`}
                        title="Affiche aussi les punchs test (is_test_punch=true) créés via le bouton « Test pause PM »"
                        data-testid="btn-toggle-include-test-punches"
                      >
                        <span>🧪</span>
                        <span>{includeTestPunches ? `Avec tests (${testCount})` : `Inclure tests${testCount > 0 ? ` (${testCount})` : ''}`}</span>
                      </button>
                      {testCount > 0 && (
                        <button
                          type="button"
                          onClick={() => setShowBulkDeleteTestDialog(true)}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 transition-colors"
                          title={`Supprimer les ${testCount} punch${testCount > 1 ? 's' : ''} test`}
                          data-testid="btn-bulk-delete-test-punches"
                        >
                          <Trash2 className="w-3 h-3" />
                          <span>Supprimer tests</span>
                        </button>
                      )}
                    </>
                  );
                })()}
                {/* 🆕 2026-05-08 — Filtre punchs à risque */}
                {(() => {
                  const atRiskCount = (rapport.punches || []).filter(p => !!isPunchAtRisk(p)).length;
                  if (atRiskCount === 0 && !riskFilter) return null;
                  return (
                    <button
                      type="button"
                      onClick={() => setRiskFilter(v => !v)}
                      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${riskFilter ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'}`}
                      title="Durée < 1h ou > 12h, ou pas de GPS"
                      data-testid="btn-filter-risque"
                    >
                      <span>🚩</span>
                      <span>{riskFilter ? 'Tous' : `À risque (${atRiskCount})`}</span>
                    </button>
                  );
                })()}
              </div>
            </div>

            <TabsContent value="par_employe" className="mt-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-2 font-semibold">Employé</th>
                          <th className="text-right p-2 font-semibold">Nb punches</th>
                          <th className="text-right p-2 font-semibold">Heures nettes</th>
                          {/* 🚚 [Phase v13.52] Colonnes déplacement + heures rémunérées totales */}
                          <th className="text-right p-2 font-semibold text-sky-700" data-testid="th-temps-deplacement">Déplac.</th>
                          <th className="text-right p-2 font-semibold text-emerald-700" data-testid="th-heures-remunerees">Total rémun.</th>
                          {/* 📏 [v13.63 Phase 5] Colonnes kilométrage (RBAC `paie_voir_kilometrage`) */}
                          {canViewKilometrage && (
                            <>
                              <th className="text-right p-2 font-semibold text-teal-700" data-testid="th-km-payables">Km payables</th>
                              <th className="text-right p-2 font-semibold text-teal-700" data-testid="th-km-montant">
                                Payout km
                                {kmLoading && <span className="ml-1 text-[10px] text-slate-400">…</span>}
                              </th>
                            </>
                          )}
                          <th className="text-center p-2 font-semibold text-xs">Historique</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rapport.par_employe?.map((e, i) => (
                          <EmployeRow
                            key={e.id || e._id || i}
                            emp={e}
                            allPunches={rapport.punches || []}
                            showKm={canViewKilometrage}
                            km={kmParEmploye[e.employee_id] || null}
                          />
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 bg-green-50">
                          <td className="p-2 font-bold">TOTAL</td>
                          <td className="p-2 text-right font-bold">{rapport.total}</td>
                          <td className="p-2 text-right font-bold text-green-800">{fmtH(rapport.total_heures_nettes)}</td>
                          {/* 🚚 [Phase v13.52] Totaux déplacement + rémunérées */}
                          <td className="p-2 text-right font-bold text-sky-800" data-testid="tfoot-total-deplacement">
                            {(rapport.total_temps_deplacement_minutes || 0) > 0
                              ? `${rapport.total_temps_deplacement_minutes} min`
                              : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="p-2 text-right font-bold text-emerald-800" data-testid="tfoot-total-remunere">
                            {fmtH(rapport.total_heures_remunerees ?? rapport.total_heures_nettes)}
                          </td>
                          {/* 📏 [v13.63 Phase 5] Totaux kilométrage */}
                          {canViewKilometrage && (
                            <>
                              <td className="p-2 text-right font-bold text-teal-800" data-testid="tfoot-total-km-payables">
                                {kmTotaux.total_payable_km > 0
                                  ? `${kmTotaux.total_payable_km.toFixed(2)} km`
                                  : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="p-2 text-right font-bold text-teal-800" data-testid="tfoot-total-km-montant">
                                {kmTotaux.total_montant_dollars > 0
                                  ? `${kmTotaux.total_montant_dollars.toFixed(2)} $`
                                  : <span className="text-slate-300">—</span>}
                              </td>
                            </>
                          )}
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="detail" className="mt-4">
              <Card>
                <CardContent className="pt-4">
                  {rapport.punches?.length === 0 ? (
                    <p className="text-center text-slate-400 py-8">Aucun pointage validé pour cette période.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left p-2">Date</th>
                            <th className="text-left p-2">Employé</th>
                            <th className="text-left p-2">Punch IN</th>
                            <th className="text-left p-2">Punch OUT</th>
                            <th className="text-right p-2">Pause</th>
                            <th className="text-right p-2">Heures nettes</th>
                            <th className="text-right p-2 hidden md:table-cell" data-testid="th-total-jour" title="Total des heures nettes pour cet employé sur cette journée (gras si ≥ 7h25 = seuil pause PM)">Total jr</th>
                            <th className="text-left p-2">Type</th>
                            <th className="text-left p-2">Bon de travail</th>
                            <th className="text-center p-2">Régime</th>
                            {isOwner && <th className="text-center p-2 w-10"></th>}
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            // 🆕 [Phase 80.2] Filtre is_test_punch (caché par défaut)
                            const baseList = (rapport.punches || []).filter(p => includeTestPunches || !p.is_test_punch);
                            const filteredRisk = riskFilter ? baseList.filter(p => !!isPunchAtRisk(p)) : baseList;
                            // 🆕 [v13.50 — Item 2] Recherche libre (client-side, case-insensitive)
                            // Cherche dans les champs suivants : notes_validation, note_employe,
                            // employee_nom, client_nom, nom_dossier, numero_bt.
                            const t = (filtreTexte || '').trim().toLowerCase();
                            if (!t) return filteredRisk;
                            return filteredRisk.filter(p => {
                              const hay = [
                                p.notes_validation, p.note_employe, p.employee_nom,
                                p.client_nom, p.nom_dossier, p.numero_bt,
                              ].filter(Boolean).map(v => String(v).toLowerCase()).join(' | ');
                              return hay.includes(t);
                            });
                          })()?.map((p, i) => (
                            p.type_punch === 'ferie_cnesst' ? (
                              // ── Ligne Jour Férié CNESST ──
                              <tr key={p.id || `ferie-${i}`} className="border-b bg-yellow-50 hover:bg-yellow-100">
                                <td className="p-2 text-slate-600">{p.punch_in_time?.slice(0, 10)}</td>
                                <td className="p-2">
                                  <div className="flex items-center gap-1.5">
                                    <EmployeeAvatar photoUrl={p.employee_photo_url} prenom={p.employee_nom?.split(' ')[0] || ''} nom={p.employee_nom?.split(' ').slice(1).join(' ') || ''} size="xs" />
                                    <span className="font-medium">{p.employee_nom}</span>
                                  </div>
                                </td>
                                <td colSpan={2} className="p-2">
                                  <span className="inline-flex items-center gap-1 bg-yellow-200 text-yellow-900 text-xs font-semibold px-2 py-0.5 rounded-full">
                                    Jour Férié — {p.nom_ferie}
                                  </span>
                                  {p.flag_admissibilite === 'a_verifier' && (
                                    <span
                                      className="ml-2 inline-flex items-center gap-1 bg-orange-100 text-orange-800 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                                      title={p.motif_admissibilite || 'Absence détectée le jour avant/après'}
                                      data-testid={`ferie-flag-${p.id}`}
                                    >
                                      ⚠️ À vérifier
                                    </span>
                                  )}
                                </td>
                                <td className="p-2 text-right text-yellow-700 text-xs font-semibold">
                                  {p.heures_ferie ? `${(p.heures_ferie || 0).toFixed(1)}h` : '—'}
                                </td>
                                <td className="p-2 text-right font-bold text-yellow-700">
                                  {(p.indemnite_montant || 0).toFixed(2)} $
                                </td>
                                <td className="p-2 hidden md:table-cell" data-testid={`td-total-jour-ferie-${p.id}`}></td>
                                <td className="p-2">
                                  <span className="text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded">CNESST</span>
                                </td>
                                <td className="p-2 text-xs text-slate-400">1/20 · {p.nb_semaines_reference}sem</td>
                                {isOwner && <td></td>}
                              </tr>
                            ) : (() => {
                            const riskReasons = isPunchAtRisk(p);
                            const riskLabel = riskReasons ? riskReasons.map(r => r === 'admin' ? 'Admin sans BT' : r === 'duree' ? 'Durée anormale' : r === 'gps' ? 'Pas de GPS' : r).join(' · ') : '';
                            return (
                            <tr key={p.id || p._id || i} className={`border-b hover:bg-slate-50 ${confirmDeleteId === p.id ? 'bg-red-50' : (riskReasons ? 'bg-red-50/40' : '')}`}>
                              <td className="p-2 text-slate-600">
                                <div className="flex items-center gap-1">
                                  {/* 🆕 [5.6] Bouton "Voir détails" — ouvre le drawer latéral */}
                                  <button
                                    onClick={() => setDrawerPunch(p)}
                                    className="text-slate-400 hover:text-blue-600 transition-colors shrink-0"
                                    title="Voir tous les détails du punch (incluant carte GPS)"
                                    data-testid={`btn-details-punch-${p.id}`}
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                  </button>
                                  {/* 🆕 [Phase 47] Mini-icône GPS directe — ouvre Google Maps si coords présentes */}
                                  {p.punch_in_latitude && p.punch_in_longitude && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(
                                          `https://www.google.com/maps?q=${p.punch_in_latitude},${p.punch_in_longitude}`,
                                          '_blank'
                                        );
                                      }}
                                      className={`shrink-0 transition-colors ${
                                        p.geo_check_in?.hors_zone === true || p.geo_check?.hors_zone === true
                                          ? 'text-red-500 hover:text-red-700'
                                          : 'text-emerald-500 hover:text-emerald-700'
                                      }`}
                                      title={
                                        p.geo_check_in?.hors_zone === true || p.geo_check?.hors_zone === true
                                          ? '🚨 Hors zone — ouvrir Google Maps'
                                          : '📍 Voir sur Google Maps'
                                      }
                                      data-testid={`btn-gps-direct-${p.id}`}
                                    >
                                      📍
                                    </button>
                                  )}
                                  {/* Phase 49 — badge "Hors zone" retiré de la table : l'épingle 📍 rouge communique déjà l'info (info reste dans le drawer Carte/Détails) */}
                                  {riskReasons && (
                                    <span title={`À risque : ${riskLabel}`} className="text-red-500" data-testid={`badge-risque-${p.id}`}>🚩</span>
                                  )}
                                  {p.punch_in_time?.slice(0, 10)}
                                  {/* 🆕 [v13.50 — Item 2] Icône 💬 : note du propriétaire à la
                                      validation. Tooltip HTML natif au hover. Le contenu complet
                                      s'affiche via `title`. */}
                                  {p.notes_validation && String(p.notes_validation).trim() && (
                                    <span
                                      title={`💬 Note propriétaire :\n${p.notes_validation}`}
                                      className="ml-0.5 text-blue-500 hover:text-blue-700 cursor-help shrink-0"
                                      data-testid={`icon-note-owner-${p.id}`}
                                      aria-label="Note du propriétaire présente"
                                    >
                                      <MessageSquare className="w-3.5 h-3.5" />
                                    </span>
                                  )}
                                  {/* 🆕 [v13.50 — Item 4] Icône 📝 : historique des commentaires
                                      employé ⇄ propriétaire. Ouvre un dialog dédié au clic. */}
                                  {p.has_historique_modifications && (
                                    <button
                                      type="button"
                                      onClick={() => setHistoriquePunch({
                                        id: p.id,
                                        label: `${p.employee_nom || 'Employé'} · ${p.punch_in_time?.slice(0, 10) || ''}`,
                                      })}
                                      className="ml-0.5 text-purple-500 hover:text-purple-700 transition-colors shrink-0"
                                      title="Voir l'historique des commentaires (employé ⇄ propriétaire)"
                                      data-testid={`icon-historique-modifs-${p.id}`}
                                      aria-label="Voir l'historique des commentaires"
                                    >
                                      <ClipboardList className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="p-2">
                                <div className="flex items-center gap-1.5">
                                  <EmployeeAvatar photoUrl={p.employee_photo_url} prenom={p.employee_nom?.split(' ')[0] || ''} nom={p.employee_nom?.split(' ').slice(1).join(' ') || ''} size="xs" />
                                  <span className="font-medium">{p.employee_nom || p.employee_id}</span>
                                  {/* 🆕 [Phase 80.2 — 2026-06-22] Badge "🧪 TEST" + tooltip metadata audit */}
                                  {p.is_test_punch && (
                                    <span
                                      className="ml-1 text-[10px] text-orange-700 bg-orange-100 border border-orange-300 px-1.5 py-0.5 rounded font-bold animate-pulse"
                                      title={(() => {
                                        const m = p.pause_pm_test_metadata || {};
                                        const lines = [
                                          '🧪 PUNCH DE TEST (Phase 80.1)',
                                          `Déclenché par : ${m.triggered_by_name || '—'}`,
                                          `Scénario : ${m.scenario_played || '—'}`,
                                          `Réponse simulée : ${m.reponse_simulee || 'N/A'}`,
                                          `Déclenché le : ${m.triggered_at ? new Date(m.triggered_at).toLocaleString('fr-CA') : '—'}`,
                                          '',
                                          'Aucun impact sur paie / bonus / stats.',
                                        ];
                                        return lines.join('\n');
                                      })()}
                                      data-testid={`badge-rapport-test-${p.id}`}
                                    >
                                      🧪 TEST
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="p-2 text-green-700 font-mono">{fmtTime(p.punch_in_time)}</td>
                              <td className="p-2 text-red-600 font-mono">{p.punch_out_time ? fmtTime(p.punch_out_time) : <Badge variant="outline" className="text-xs">En cours</Badge>}</td>
                              <td className="p-2 text-right text-amber-700">
                                {(p.heures_pause || 0) > 0 ? (
                                  <div className="flex flex-col items-end gap-0.5" data-testid={`pause-info-${p.id}`}>
                                    <span className="font-medium" data-testid={`pause-prise-${p.id}`}>{((p.heures_pause || 0) * 60).toFixed(0)}m prise</span>
                                    {/* 🆕 [Phase 44 #1] Affichage cohérent : on montre TOUJOURS un 2e badge
                                        complémentaire pour clarifier le statut de la pause (déduite / payée /
                                        non décomptée). Avant : badge « déduite » disparaissait si valeur=0
                                        → utilisatrice voyait juste « 30m prise » sans contexte.
                                        🆕 [Phase v13.60.6 — 2026-08-14] Fallback intelligent : si le backend
                                        n'a pas persisté `pause_deduction` (cas historique des punches importés
                                        pré-v13.60.6), on dérive la valeur depuis `heures_pause - pause_payee`.
                                        La valeur backend reste PRIORITAIRE quand présente. Pattern aligné avec
                                        `ValidationHeures.jsx` L 600. Cf. rca_v13_60_6_import_pause_non_deduite.md. */}
                                    {(() => {
                                      const pauseDedH = (p.pause_deduction != null)
                                        ? p.pause_deduction
                                        : ((p.heures_pause || 0) - (p.pause_payee || 0));
                                      const pauseDedFinal = pauseDedH > 0 ? pauseDedH : 0;
                                      return pauseDedFinal > 0 ? (
                                      <span className="text-xs text-red-600" data-testid={`pause-deduite-${p.id}`}>
                                        -{(pauseDedFinal * 60).toFixed(0)}m déduite
                                      </span>
                                    ) : (p.pause_payee || 0) > 0 ? (
                                      <span className="text-xs text-green-600" data-testid={`pause-payee-${p.id}`}>
                                        +{((p.pause_payee || 0) * 60).toFixed(0)}m payée
                                      </span>
                                    ) : (
                                      <span className="text-xs text-slate-500" data-testid={`pause-non-decomptee-${p.id}`}>
                                        0m déduite (non décomptée)
                                      </span>
                                      );
                                    })()}
                                    {/* Si pause à la fois déduite ET payée (cas mixte rare) → on affiche les 2 */}
                                    {(p.pause_deduction || 0) > 0 && (p.pause_payee || 0) > 0 && (
                                      <span className="text-xs text-green-600">
                                        +{((p.pause_payee || 0) * 60).toFixed(0)}m payée
                                      </span>
                                    )}
                                    {p.pause_ajustee_manuellement && (
                                      <span
                                        className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-1 py-0.5"
                                        title={`Pause ajustée manuellement${p.pause_ajustee_par ? ` par ${p.pause_ajustee_par}` : ''}${p.pause_ajustee_le ? ` le ${p.pause_ajustee_le.slice(0,10)}` : ''}`}
                                        data-testid={`badge-pause-manuelle-${p.id}`}
                                      >
                                        ✎ ajustée
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  p.pause_ajustee_manuellement ? (
                                    <span
                                      className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 rounded px-1 py-0.5"
                                      title={`Pause mise à 0 manuellement${p.pause_ajustee_par ? ` par ${p.pause_ajustee_par}` : ''}${p.pause_ajustee_le ? ` le ${p.pause_ajustee_le.slice(0,10)}` : ''}`}
                                      data-testid={`badge-pause-manuelle-${p.id}`}
                                    >
                                      0m (ajustée)
                                    </span>
                                  ) : '—'
                                )}
                              </td>
                              <td className="p-2 text-right font-bold">
                                <div className="flex flex-col items-end gap-0.5">
                                  <span>{fmtH(p.heures_nettes || 0)}</span>
                                  {/* 🆕 [Phase 40 — P0 #2] Badge Pause PM (15 min après-midi) */}
                                  {p.pause_pm_applicable && p.pause_pm_prise === false && (
                                    <span
                                      className="text-[10px] text-amber-700 bg-amber-50 border border-amber-300 rounded px-1 py-0.5 font-medium"
                                      title="Employé a déclaré ne PAS avoir pris sa pause de 15 min en après-midi. +15 min ajoutées."
                                      data-testid={`badge-pause-pm-non-prise-${p.id}`}
                                    >
                                      +15 min PM
                                    </span>
                                  )}
                                  {p.pause_pm_applicable && p.pause_pm_prise === true && (
                                    <span
                                      className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-300 rounded px-1 py-0.5"
                                      title="Pause de 15 min PM prise"
                                      data-testid={`badge-pause-pm-prise-${p.id}`}
                                    >
                                      ☕ PM prise
                                    </span>
                                  )}
                                  {p.pause_pm_applicable && p.pause_pm_prise === null && p.statut === 'complete' && (
                                    <span
                                      className="text-[10px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-1 py-0.5"
                                      title="Pause PM applicable mais non déclarée au dépunch"
                                      data-testid={`badge-pause-pm-non-declaree-${p.id}`}
                                    >
                                      PM ?
                                    </span>
                                  )}
                                </div>
                              </td>
                              {/* 🆕 [Phase 49] Total des heures de la journée pour cet employé.
                                  Gras + couleur si ≥ seuil 7h25 (déclencheur règle pause PM agrégée). */}
                              <td className="p-2 text-right hidden md:table-cell" data-testid={`td-total-jour-${p.id}`}>
                                {(() => {
                                  const key = `${p.employee_id}_${(p.punch_in_time || '').slice(0,10)}`;
                                  const total = totauxParJour[key] || 0;
                                  if (total <= 0) return <span className="text-slate-300">—</span>;
                                  const atSeuil = total >= SEUIL_TOTAL_JOUR_H;
                                  return (
                                    <span
                                      className={atSeuil ? 'font-bold text-emerald-700' : 'text-slate-600'}
                                      title={atSeuil ? `≥ 7h25 → règle pause PM applicable (Phase 49)` : `< 7h25`}
                                    >
                                      {fmtH(total)}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td className="p-2">
                                <div className="flex items-center gap-1">
                                  <Badge variant="outline" className={p.type_punch === 'admin' ? 'border-purple-300 text-purple-700' : 'border-green-300 text-green-700'}>
                                    {p.type_punch || 'chantier'}
                                  </Badge>
                                  {p.auto_modifie && (
                                    <Badge variant="outline" className="border-orange-400 text-orange-700 bg-orange-50 text-[10px]" data-testid={`badge-auto-modifie-${p.id}`}>
                                      Modifié par {p.auto_modifie_par || 'employé'}
                                    </Badge>
                                  )}
                                </div>
                              </td>
                              <td className="p-2 text-xs">
                                {p.bon_travail_id ? (
                                  <div className="flex flex-col">
                                    <span className="font-medium text-slate-700 break-words leading-snug min-w-[220px]" title={`${p.client_nom || ''}${p.nom_dossier ? ' — ' + p.nom_dossier : ''}`}>
                                      {p.client_nom || <span className="text-slate-400 italic">Client inconnu</span>}
                                    </span>
                                    {p.nom_dossier && (
                                      <span className="text-[10px] text-slate-500 break-words leading-snug min-w-[180px]" title={p.nom_dossier}>
                                        {p.nom_dossier}
                                      </span>
                                    )}
                                    {p.numero_bt && (
                                      <span className="text-[10px] text-slate-400">BT #{p.numero_bt}</span>
                                    )}
                                    {p.segmente_origine && (
                                      <span className="text-[10px] text-blue-600" data-testid={`badge-segmente-${p.id}`}>✂️ Segmenté</span>
                                    )}
                                    {p.segmente_depuis && (
                                      <span className="text-[10px] text-blue-600" data-testid={`badge-depuis-split-${p.id}`}>↳ 2e segment</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              {/* 🆕 2026-05-06 Phase 6 Module CCQ — colonne Régime */}
                              <td className="p-2 text-center">
                                {p.bt_regime_ccq ? (
                                  <RegimeBadge
                                    regime={p.bt_regime_ccq}
                                    requiresReview={p.ccq_snapshot?.requires_ccq_grade_review === true}
                                  />
                                ) : (
                                  <span className="text-slate-300 text-xs">—</span>
                                )}
                              </td>
                              {isOwner && (
                                <td className="p-2 text-center">
                                  <div className="flex items-center justify-center gap-0.5">
                                    {p.statut === 'valide' && p.punch_out_time && (
                                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                        onClick={() => openSplitDialog(p)}
                                        title="Modifier le punch"
                                        data-testid={`btn-segmenter-${p.id}`}>
                                        <SquarePen className="w-3.5 h-3.5" />
                                      </Button>
                                    )}
                                    {/* 🆕 2026-05-11 — Bouton "Payer cash" (propriétaire uniquement) */}
                                    {p.statut === 'valide' && p.punch_out_time && (
                                      <Button size="sm" variant="ghost"
                                        className={`h-6 w-6 p-0 ${p.heures_cash > 0 ? 'text-emerald-600 hover:text-emerald-700 bg-emerald-50' : 'text-amber-500 hover:text-amber-700 hover:bg-amber-50'}`}
                                        onClick={() => setCashDialog({ punch: p })}
                                        title={p.heures_cash > 0 ? `${p.heures_cash}h payées cash (${p.montant_cash}$)` : 'Marquer des heures comme payées cash'}
                                        data-testid={`btn-cash-${p.id}`}>
                                        <span className="text-sm font-bold">$</span>
                                      </Button>
                                    )}
                                    {confirmDeleteId === p.id ? (
                                      <div className="flex items-center gap-1">
                                        <Button size="sm" variant="destructive" className="h-6 px-2 text-xs"
                                          onClick={() => handleDeletePunch(p.id)} disabled={deletingId === p.id}
                                          data-testid={`btn-confirmer-suppression-rapport-${p.id}`}>
                                          {deletingId === p.id ? '...' : 'Oui'}
                                        </Button>
                                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs"
                                          onClick={() => setConfirmDeleteId(null)}>
                                          Non
                                        </Button>
                                      </div>
                                    ) : (
                                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-red-600"
                                        onClick={() => setConfirmDeleteId(p.id)} title="Supprimer"
                                        data-testid={`btn-supprimer-rapport-${p.id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                            );
                            })()
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}

      {searched && rapport?.total === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-slate-400">
            <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aucun pointage validé trouvé pour ces critères.</p>
          </CardContent>
        </Card>
      )}

      {/* 🆕 Dialog assigner / modifier / segmenter un punch */}
      <Dialog open={!!splitPunch} onOpenChange={(v) => !v && setSplitPunch(null)}>
        <DialogContent className="max-w-2xl" data-testid="dialog-segmenter-punch">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scissors className="w-5 h-5 text-blue-600" />
              {splitMode === 'assign'
                ? (splitPunch?.bon_travail_id ? 'Réassigner le punch à un autre BT' : 'Assigner le punch à un BT')
                : splitMode === 'edit'
                  ? 'Modifier le punch'
                  : 'Segmenter le punch en 2 punchs'}
            </DialogTitle>
          </DialogHeader>

          {splitPunch && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              <div className="p-3 bg-slate-50 border rounded-lg text-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{splitPunch.employee_nom}</p>
                    <p className="text-xs text-slate-500">
                      {splitPunch.punch_in_time?.slice(0, 10)} · {fmtTime(splitPunch.punch_in_time)} → {fmtTime(splitPunch.punch_out_time)} ({fmtH(splitPunch.heures_nettes)})
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      État actuel : {splitPunch.bon_travail_id ? (
                        <>
                          <span className="font-medium">{splitPunch.client_nom || 'Client inconnu'}</span>
                          {splitPunch.numero_bt ? ` · BT #${splitPunch.numero_bt}` : ''}
                        </>
                      ) : (
                        <span className="font-medium text-purple-700">Admin (sans BT)</span>
                      )}
                    </p>
                  </div>
                  {/* 🆕 2026-05-08 — Bouton d'historique d'audit */}
                  <div className="flex items-center gap-2">
                    {/* 🆕 [Phase 46] Bouton pour ouvrir le drawer complet (carte géoloc + détails) */}
                    <button
                      type="button"
                      onClick={() => setDrawerPunch(splitPunch)}
                      className="text-xs text-slate-500 hover:text-blue-600 underline whitespace-nowrap"
                      data-testid="btn-ouvrir-drawer-geo"
                      title="Voir la géolocalisation, la carte, et tous les détails du punch"
                    >
                      📍 Carte / détails
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const next = !showAudit;
                        setShowAudit(next);
                        if (next && auditLog === null) loadAuditLog(splitPunch.id);
                      }}
                      className="text-xs text-slate-500 hover:text-blue-600 underline whitespace-nowrap"
                      data-testid="btn-toggle-audit"
                    >
                      {showAudit ? 'Masquer historique' : '📜 Historique'}
                    </button>
                  </div>
                </div>
                {showAudit && (
                  <div className="mt-2 pt-2 border-t border-slate-200" data-testid="audit-log-panel">
                    {auditLoading && <p className="text-xs text-slate-400 italic">Chargement…</p>}
                    {!auditLoading && auditLog && auditLog.length === 0 && (
                      <p className="text-xs text-slate-400 italic">Aucune modification enregistrée pour ce punch.</p>
                    )}
                    {!auditLoading && auditLog && auditLog.length > 0 && (
                      <ul className="space-y-1.5 text-[11px] max-h-40 overflow-y-auto">
                        {auditLog.map((log, idx) => (
                          <li key={log.id || idx} className="border-l-2 border-blue-300 pl-2">
                            <div className="flex items-center gap-1.5 text-slate-600">
                              <span className="font-semibold">{log.actor_name || '?'}</span>
                              <span className="text-slate-400">·</span>
                              <span>{log.action}</span>
                              <span className="text-slate-400 ml-auto">
                                {formatLocalTime(log.date, { format: 'short' })}
                              </span>
                            </div>
                            {Array.isArray(log.changes) && log.changes.map((c, i) => (
                              <p key={i} className="text-slate-500 ml-1">
                                — {/* 🆕 [Phase 45] Convertir les timestamps ISO UTC en heure locale Québec
                                       (cohérent avec le header local time du dialog). */}
                                {String(c).replace(/(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?Z/g, (match, _d) => {
                                  try {
                                    const dt = new Date(match);
                                    return dt.toLocaleString('fr-CA', {
                                      timeZone: 'America/Toronto',
                                      year: 'numeric', month: '2-digit', day: '2-digit',
                                      hour: '2-digit', minute: '2-digit', hour12: false,
                                    }).replace(',', '') + ' (local)';
                                  } catch {
                                    return match;
                                  }
                                })}
                              </p>
                            ))}
                            {log.notes && <p className="text-slate-400 italic ml-1">« {log.notes} »</p>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {/* 🆕 Toggle entre les 3 modes */}
              <div className="grid grid-cols-3 gap-2 p-1 bg-slate-100 rounded-lg" data-testid="split-mode-toggle">
                <button
                  type="button"
                  onClick={() => setSplitMode('assign')}
                  className={`text-xs sm:text-sm py-2 px-2 rounded-md transition-colors ${splitMode === 'assign' ? 'bg-white shadow-sm font-semibold text-blue-700' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="btn-mode-assign"
                >
                  {splitPunch?.bon_travail_id ? '🔄 Réassigner' : '📌 Assigner au BT'}
                </button>
                <button
                  type="button"
                  onClick={() => setSplitMode('edit')}
                  className={`text-xs sm:text-sm py-2 px-2 rounded-md transition-colors ${splitMode === 'edit' ? 'bg-white shadow-sm font-semibold text-blue-700' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="btn-mode-edit"
                >
                  ✏️ Modifier
                </button>
                <button
                  type="button"
                  onClick={() => setSplitMode('split')}
                  className={`text-xs sm:text-sm py-2 px-2 rounded-md transition-colors ${splitMode === 'split' ? 'bg-white shadow-sm font-semibold text-blue-700' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid="btn-mode-split"
                >
                  ✂️ Segmenter
                </button>
              </div>

              {/* === MODE ASSIGN === */}
              {splitMode === 'assign' && (
                <>
                  <div>
                    <Label className="text-xs text-slate-600">
                      {splitPunch.bon_travail_id ? 'Nouveau Bon de Travail (réassignation) *' : 'Bon de Travail à assigner *'}
                    </Label>
                    <BtSearchCombobox
                      value={splitNewBtId}
                      onChange={(id) => setSplitNewBtId(id || '')}
                      referenceDate={splitPunch.punch_in_time?.slice(0, 10)}
                      excludeId={splitPunch.bon_travail_id || undefined}
                      autoFocus
                      testIdPrefix="split-bt"
                    />
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
                    <p className="font-medium mb-1">Résumé :</p>
                    {splitPunch.bon_travail_id ? (
                      <p>• Tout le punch ({fmtTime(splitPunch.punch_in_time)} → {fmtTime(splitPunch.punch_out_time)}, {fmtH(splitPunch.heures_nettes)}) sera <b>réassigné</b>. Heures réelles des BT ajustées automatiquement.</p>
                    ) : (
                      <p>• Tout le punch ({fmtTime(splitPunch.punch_in_time)} → {fmtTime(splitPunch.punch_out_time)}, {fmtH(splitPunch.heures_nettes)}) sera assigné au BT choisi.</p>
                    )}
                    {splitPunch.type_punch === 'admin' && <p className="mt-1">Le type passera de <b>admin</b> → <b>chantier</b>.</p>}
                  </div>
                </>
              )}

              {/* === MODE EDIT === */}
              {splitMode === 'edit' && (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs text-slate-600">Heure début *</Label>
                      <Input type="time" value={editForm.punch_in} onChange={(e) => setEditForm(f => ({ ...f, punch_in: e.target.value }))} data-testid="edit-punch-in" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Heure fin *</Label>
                      <Input type="time" value={editForm.punch_out} onChange={(e) => setEditForm(f => ({ ...f, punch_out: e.target.value }))} data-testid="edit-punch-out" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Pause (min)</Label>
                      <Input type="number" min="0" max="600" value={editForm.pause_min} onChange={(e) => setEditForm(f => ({ ...f, pause_min: e.target.value }))} data-testid="edit-pause" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Bon de travail (vide = admin)</Label>
                    <BtSearchCombobox
                      value={editForm.bt_id}
                      onChange={(id) => setEditForm(f => ({ ...f, bt_id: id || '' }))}
                      referenceDate={splitPunch.punch_in_time?.slice(0, 10)}
                      testIdPrefix="edit-bt"
                    />
                  </div>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                    <p>⚠️ Cette modification écrase les heures du punch existant. Type {editForm.bt_id ? 'chantier' : 'admin'} (auto-déduit). Les heures réelles du BT seront ajustées.</p>
                  </div>
                </>
              )}

              {/* === MODE SPLIT (2+ segments) === */}
              {splitMode === 'split' && (
                <>
                  {splitSegments.map((seg, idx) => {
                    const heuresBrutes = seg.punch_in && seg.punch_out
                      ? Math.max(0, (new Date(`2000-01-01T${seg.punch_out}:00`) - new Date(`2000-01-01T${seg.punch_in}:00`)) / 3600000)
                      : 0;
                    const heuresNettes = Math.max(0, heuresBrutes - (Number(seg.pause_min) || 0) / 60);
                    return (
                      <div key={idx} className="border-l-4 border-blue-400 pl-3 py-2 bg-blue-50/30 rounded-r" data-testid={`split-segment-${idx}`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-semibold text-sm text-blue-900">
                            Segment {idx + 1} {seg.bt_id ? '🏗️ Chantier' : '🟣 Admin'}
                            {heuresNettes > 0 && <span className="text-xs font-normal text-slate-500 ml-2">({heuresNettes.toFixed(2)}h nettes)</span>}
                          </p>
                          {/* 🆕 2026-05-12 — Bouton supprimer (min 2 segments requis) */}
                          {splitSegments.length > 2 && (
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setSplitSegments(prev => prev.filter((_, i) => i !== idx))}
                              data-testid={`btn-remove-segment-${idx}`}
                              title="Supprimer ce segment">
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-2 mb-2">
                          <div>
                            <Label className="text-[10px] text-slate-500">Début *</Label>
                            <Input type="time" value={seg.punch_in} onChange={(e) => updateSegment(idx, 'punch_in', e.target.value)} data-testid={`seg-${idx}-in`} />
                          </div>
                          <div>
                            <Label className="text-[10px] text-slate-500">Fin *</Label>
                            <Input type="time" value={seg.punch_out} onChange={(e) => updateSegment(idx, 'punch_out', e.target.value)} data-testid={`seg-${idx}-out`} />
                          </div>
                          <div>
                            <Label className="text-[10px] text-slate-500">Pause (min)</Label>
                            <Input type="number" min="0" max="600" value={seg.pause_min} onChange={(e) => updateSegment(idx, 'pause_min', e.target.value)} data-testid={`seg-${idx}-pause`} />
                          </div>
                        </div>
                        <Label className="text-[10px] text-slate-500">Bon de travail (vide = admin)</Label>
                        <BtSearchCombobox
                          value={seg.bt_id}
                          onChange={(id) => updateSegment(idx, 'bt_id', id || '')}
                          referenceDate={splitPunch.punch_in_time?.slice(0, 10)}
                          testIdPrefix={`seg-${idx}-bt`}
                        />
                      </div>
                    );
                  })}
                  {/* 🆕 2026-05-12 — Bouton « + Ajouter un segment » */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-dashed border-blue-300 text-blue-700 hover:bg-blue-50"
                    onClick={() => {
                      setSplitSegments(prev => {
                        const last = prev[prev.length - 1];
                        // Le nouveau segment démarre à la fin du précédent et finit à l'heure de fin du punch
                        const newSeg = {
                          punch_in: last.punch_out || '',
                          punch_out: toTimeStr(splitPunch.punch_out_time),
                          pause_min: 0,
                          bt_id: splitPunch.bon_travail_id || '',
                        };
                        return [...prev, newSeg];
                      });
                    }}
                    data-testid="btn-add-segment"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Ajouter un segment
                  </Button>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
                    <p>ℹ️ Les segments doivent rester dans la fenêtre {fmtTime(splitPunch.punch_in_time)} → {fmtTime(splitPunch.punch_out_time)}. Trous autorisés (ex : pause repas longue). Type chantier/admin déduit auto selon présence du BT. Heures réelles des BT ajustées partout (Mon Espace, Stats dossier, paie, bonus).</p>
                  </div>
                </>
              )}

              <div>
                <Label className="text-xs text-slate-600">Notes (optionnel)</Label>
                <Input
                  value={splitNotes}
                  onChange={(e) => setSplitNotes(e.target.value)}
                  placeholder="Ex: Oubli de dé-puncher entre 2 BT"
                  data-testid="input-split-notes"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSplitPunch(null)} disabled={splitLoading}>Annuler</Button>
            <Button
              onClick={submitSplit}
              disabled={
                splitLoading
                || (splitMode === 'assign' && !splitNewBtId)
                || (splitMode === 'edit' && (!editForm.punch_in || !editForm.punch_out))
                || (splitMode === 'split' && splitSegments.some(s => !s.punch_in || !s.punch_out))
              }
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="btn-confirmer-segmentation"
            >
              <Scissors className="w-4 h-4 mr-2" />
              {splitLoading ? 'En cours…'
                : splitMode === 'assign' ? (splitPunch?.bon_travail_id ? 'Réassigner au BT' : 'Assigner au BT')
                : splitMode === 'edit' ? 'Enregistrer la modification'
                : `Segmenter (${splitSegments.length} segments)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 2026-05-11 — Dialog Cash Payment · 🆕 Phase 105 (2026-07-09) — Enrichi avec prime horaire ponctuelle */}
      {cashDialog?.punch && (
        <Dialog open={!!cashDialog} onOpenChange={(o) => !o && setCashDialog(null)}>
          <DialogContent className="max-w-lg" data-testid="dialog-cash-payment">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="text-emerald-600 font-bold text-xl">$</span> Paiement cash & prime
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div className="bg-slate-50 rounded p-3 text-sm">
                <p className="font-semibold">{cashDialog.punch.employee_nom}</p>
                <p className="text-xs text-slate-600">
                  {formatLocalTime(cashDialog.punch.punch_in_time, { dateOnly: true, weekday: 'long', withoutYear: true })} —{' '}
                  {cashDialog.punch.heures_nettes?.toFixed(2) || 0}h nettes
                </p>
                {cashDialog.punch.client_nom && (
                  <p className="text-xs text-slate-500 mt-0.5">Client : {cashDialog.punch.client_nom}</p>
                )}
              </div>

              {/* Paiements existants */}
              {(cashDialog.punch.paiements_cash || []).length > 0 && (
                <div className="border rounded p-2 bg-emerald-50/40">
                  <p className="text-xs font-semibold text-emerald-800 mb-1">Paiements cash déjà enregistrés :</p>
                  <ul className="space-y-1 text-xs">
                    {cashDialog.punch.paiements_cash.map(pc => (
                      <li key={pc.id} className="flex items-center justify-between gap-2 bg-white px-2 py-1 rounded">
                        <span>
                          <b>{pc.heures}h</b> × {pc.taux_horaire}$/h = <b>{pc.montant}$</b>
                          {(pc.prime_horaire || 0) > 0 && (
                            <span className="text-amber-700 ml-1">
                              + prime {pc.prime_horaire}$/h ({pc.prime_montant}$, {pc.prime_mode_paiement === 'cash' ? 'cash' : 'chèque'}{pc.prime_est_declaree ? ' déclarée' : ''})
                            </span>
                          )}
                          {pc.note && <span className="text-slate-500"> — {pc.note}</span>}
                        </span>
                        <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-500 hover:text-red-700"
                          disabled={cashLoading}
                          onClick={() => handleRemoveCashPayment(cashDialog.punch.id, pc.id)}
                          data-testid={`btn-remove-cash-${pc.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Phase 105 — Radio type paiement */}
              <div className="border border-slate-200 rounded p-2 bg-white">
                <Label className="text-xs font-semibold text-slate-700 block mb-1.5">Type de paiement</Label>
                <div className="space-y-1">
                  <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="radio-cash-simple-wrap">
                    <input type="radio" name="cash-type" value="simple"
                      checked={cashType === 'simple'}
                      onChange={() => setCashType('simple')}
                      data-testid="radio-cash-simple" />
                    <span>Paiement cash simple</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="radio-cash-avec-prime-wrap">
                    <input type="radio" name="cash-type" value="avec_prime"
                      checked={cashType === 'avec_prime'}
                      onChange={() => setCashType('avec_prime')}
                      data-testid="radio-cash-avec-prime" />
                    <span>Paiement cash + prime horaire ponctuelle</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Heures à payer cash</Label>
                  <Input type="text" inputMode="decimal" value={cashHeures}
                    onChange={(e) => setCashHeures(e.target.value)}
                    placeholder="ex: 3.5" data-testid="input-cash-heures" />
                </div>
                <div>
                  <Label className="text-xs">Taux horaire cash ($/h)</Label>
                  <Input type="text" inputMode="decimal" value={cashTaux}
                    onChange={(e) => setCashTaux(e.target.value)}
                    placeholder="ex: 25.00" data-testid="input-cash-taux" />
                </div>
              </div>

              {/* Phase 105 — Section prime conditionnelle */}
              {cashType === 'avec_prime' && (
                <div className="border border-amber-200 rounded p-2.5 bg-amber-50/40 space-y-2" data-testid="section-prime">
                  <p className="text-xs font-semibold text-amber-800">🎁 Prime horaire ponctuelle</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Prime par heure ($/h)</Label>
                      <Input type="text" inputMode="decimal" value={primeHoraire}
                        onChange={(e) => setPrimeHoraire(e.target.value)}
                        placeholder="ex: 10.00" data-testid="input-prime-horaire" />
                    </div>
                    <div>
                      <Label className="text-xs">Mode de paiement</Label>
                      <div className="flex gap-2 mt-1.5">
                        <label className="flex items-center gap-1 text-xs cursor-pointer">
                          <input type="radio" name="prime-mode" value="cash"
                            checked={primeModePaiement === 'cash'}
                            onChange={() => setPrimeModePaiement('cash')}
                            data-testid="radio-prime-mode-cash" />
                          Cash
                        </label>
                        <label className="flex items-center gap-1 text-xs cursor-pointer">
                          <input type="radio" name="prime-mode" value="cheque"
                            checked={primeModePaiement === 'cheque'}
                            onChange={() => setPrimeModePaiement('cheque')}
                            data-testid="radio-prime-mode-cheque" />
                          Chèque
                        </label>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs flex items-center gap-1">
                      Prime déclarée (avec cotisations sociales) ?
                    </Label>
                    <div className="flex gap-3 mt-1">
                      <label className="flex items-center gap-1 text-xs cursor-pointer">
                        <input type="radio" name="prime-declaree" value="non"
                          checked={!primeEstDeclaree}
                          onChange={() => setPrimeEstDeclaree(false)}
                          data-testid="radio-prime-declaree-non" />
                        Non — hors système
                      </label>
                      <label className={`flex items-center gap-1 text-xs cursor-pointer ${primeModePaiement === 'cash' ? 'opacity-50' : ''}`}>
                        <input type="radio" name="prime-declaree" value="oui"
                          checked={primeEstDeclaree}
                          onChange={() => setPrimeEstDeclaree(true)}
                          disabled={primeModePaiement === 'cash'}
                          data-testid="radio-prime-declaree-oui" />
                        Oui — sur bulletin
                        {primeModePaiement === 'cash' && <span className="text-[10px] text-slate-400">(désactivé en cash)</span>}
                      </label>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Description (optionnel)</Label>
                    <Input value={primeDescription} onChange={(e) => setPrimeDescription(e.target.value)}
                      placeholder="ex: Chantier XYZ — taux majoré" data-testid="input-prime-description" />
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs">Note admin (optionnel)</Label>
                <Input value={cashNote} onChange={(e) => setCashNote(e.target.value)}
                  placeholder="ex: bonus weekend" data-testid="input-cash-note" />
              </div>

              {/* Phase 105 — Calcul live décomposé */}
              {cashHeures && cashTaux && !isNaN(parseFloat(cashHeures)) && !isNaN(parseFloat(cashTaux)) && (
                <div className="bg-emerald-50 rounded p-2.5 text-xs space-y-1" data-testid="cash-total-preview">
                  {(() => {
                    const h = parseFloat(String(cashHeures).replace(',','.'));
                    const t = parseFloat(String(cashTaux).replace(',','.'));
                    const cashDirect = h * t;
                    const primeH = cashType === 'avec_prime' ? (parseFloat(String(primeHoraire).replace(',','.')) || 0) : 0;
                    const primeMontant = h * primeH;
                    const primeIsCash = cashType === 'avec_prime' && primeModePaiement === 'cash';
                    const primeIsCheque = cashType === 'avec_prime' && primeModePaiement === 'cheque';
                    return (
                      <>
                        <div className="flex justify-between">
                          <span>▸ Cash direct :</span>
                          <b>{h.toFixed(2)}h × {t.toFixed(2)}$ = {cashDirect.toFixed(2)} $</b>
                        </div>
                        {primeH > 0 && primeIsCash && (
                          <div className="flex justify-between text-amber-700">
                            <span>▸ Prime cash :</span>
                            <b>{h.toFixed(2)}h × {primeH.toFixed(2)}$ = {primeMontant.toFixed(2)} $</b>
                          </div>
                        )}
                        {primeH > 0 && primeIsCheque && (
                          <div className="flex justify-between text-blue-700">
                            <span>▸ Prime chèque{primeEstDeclaree ? ' (déclarée)' : ' (non déclarée)'} :</span>
                            <b>{primeMontant.toFixed(2)} $</b>
                          </div>
                        )}
                        <div className="flex justify-between border-t border-emerald-200 pt-1 font-bold text-emerald-800">
                          <span>▸ TOTAL versé :</span>
                          <span data-testid="cash-total-value">{(cashDirect + primeMontant).toFixed(2)} $</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCashDialog(null)} data-testid="btn-cash-cancel">Annuler</Button>
              <Button onClick={handleAddCashPayment} disabled={cashLoading}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-cash-confirm">
                {cashLoading ? 'En cours…' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* 🆕 [5.6] Drawer latéral "Détails du punch" */}
      <PunchDetailsDrawer
        punch={drawerPunch}
        open={!!drawerPunch}
        onClose={() => setDrawerPunch(null)}
      />

      {/* 🆕 [v13.50 — Item 4] Dialog historique conversationnel employé ⇄ propriétaire */}
      <HistoriqueCommentairesDialog
        punchId={historiquePunch?.id}
        punchLabel={historiquePunch?.label}
        open={!!historiquePunch}
        onOpenChange={(o) => { if (!o) setHistoriquePunch(null); }}
      />

      {/* 🆕 [Phase 80.2 — 2026-06-22] Dialog confirmation bulk delete punchs test (Owner-only) */}
      {isOwner && showBulkDeleteTestDialog && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !bulkDeletingTest && setShowBulkDeleteTestDialog(false)}
          data-testid="dialog-bulk-delete-test-backdrop"
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="dialog-bulk-delete-test"
          >
            <h3 className="text-lg font-semibold mb-2 text-red-700">🗑️ Supprimer tous les punchs test ?</h3>
            <p className="text-sm text-slate-600 mb-4">
              Cette action supprime <strong>tous les documents</strong> avec
              <code className="bg-slate-100 px-1 rounded text-xs"> is_test_punch=true</code> de la base de données.
              Les vrais punchs ne sont pas touchés. Cette opération est <strong>irréversible</strong>.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowBulkDeleteTestDialog(false)}
                disabled={bulkDeletingTest}
                data-testid="btn-bulk-delete-cancel"
              >
                Annuler
              </Button>
              <Button
                variant="destructive"
                onClick={handleBulkDeleteTestPunches}
                disabled={bulkDeletingTest}
                data-testid="btn-bulk-delete-confirm"
              >
                {bulkDeletingTest ? 'Suppression…' : 'Oui, supprimer tous'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

